import { useEffect, useRef } from 'react';
import './App.css';

interface PolicyPageProps {
  policyUUID: string;
  title: string;
}

const CALENDLY_URL = 'https://calendly.com/zach-lotenlabs/new-meeting';

function PolicyPage({ policyUUID, title }: PolicyPageProps): JSX.Element {
  const embedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = `${title} — Loten Labs`;

    // Create the Termly embed element with the custom `name` attribute
    if (embedRef.current) {
      embedRef.current.innerHTML = '';
      const embedDiv = document.createElement('div');
      embedDiv.setAttribute('name', 'termly-embed');
      embedDiv.setAttribute('data-id', policyUUID);
      embedDiv.setAttribute('data-type', 'iframe');
      embedRef.current.appendChild(embedDiv);
    }

    // Load the Termly embed script
    const script = document.createElement('script');
    script.src = 'https://app.termly.io/embed-policy.min.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [policyUUID, title]);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="splash-root">
      {/* ===== Header ===== */}
      <header>
        <nav className="container">
          <a href="/" className="logo">
            <img src="/mstile-150x150.png" alt="Loten Labs" className="logo-icon" />
            Loten Labs
          </a>
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="cta-button nav-cta"
          >
            Book a Demo
          </a>
        </nav>
      </header>

      <main id="main-content">
        <section className="policy-section">
          <div className="container policy-container">
            <div ref={embedRef} />
          </div>
        </section>
      </main>

      {/* ===== Footer ===== */}
      <footer>
        <div className="container footer-content">
          <div className="footer-brand">
            <img src="/mstile-150x150.png" alt="Loten Labs" className="footer-logo-icon" />
            <span className="footer-name">Loten Labs</span>
          </div>
          <div className="footer-legal">
            <a href="/privacy-policy">Privacy Policy</a>
            <span className="footer-legal__sep" aria-hidden="true">·</span>
            <a href="/cookie-policy">Cookie Policy</a>
            <span className="footer-legal__sep" aria-hidden="true">·</span>
            <a href="/terms">Terms &amp; Conditions</a>
            <span className="footer-legal__sep" aria-hidden="true">·</span>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a href="#" className="termly-display-preferences">Consent Preferences</a>
          </div>
          <p>© {new Date().getFullYear()} Loten Labs. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default PolicyPage;
